import { useCallback, useRef, useState } from 'react';
import type { ChatMessage } from './types';

// useEddyChat — owns the conversation and streams Eddy's replies.
//
// The browser only ever talks to /api/chat (the guardrail proxy). It sends the
// visible turns and receives a plain-text stream back. The system prompt,
// knowledge base, and guardrails live server-side and never reach here.

interface UseEddyChat {
  messages: ChatMessage[];
  send: (text: string) => void;
  isStreaming: boolean;
  error: string | null;
}

const API_URL = '/api/chat';

export function useEddyChat(): UseEddyChat {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || inFlight.current) return;

      inFlight.current = true;
      setError(null);
      setIsStreaming(true);

      // Snapshot history to send (excludes the placeholder we add for streaming).
      const history: ChatMessage[] = [...messages, { role: 'user', content: trimmed }];
      setMessages([...history, { role: 'assistant', content: '' }]);

      try {
        const res = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ messages: history }),
        });

        if (!res.ok || !res.body) {
          throw new Error(`Eddy is unavailable (status ${res.status}).`);
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let acc = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          acc += decoder.decode(value, { stream: true });
          // Update the last (assistant) message as chunks arrive.
          setMessages((prev) => {
            const next = [...prev];
            next[next.length - 1] = { role: 'assistant', content: acc };
            return next;
          });
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Something went wrong.';
        setError(msg);
        setMessages((prev) => {
          const next = [...prev];
          // Replace the empty placeholder with a graceful note.
          next[next.length - 1] = {
            role: 'assistant',
            content:
              "I'm having trouble reaching the team right now — please try again in a moment, or email support@edibridge.com and we'll jump on it.",
          };
          return next;
        });
      } finally {
        setIsStreaming(false);
        inFlight.current = false;
      }
    },
    [messages],
  );

  return { messages, send, isStreaming, error };
}
